import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  FileText,
  Loader2,
  RefreshCw,
  Edit3,
  Check,
  Link as LinkIcon,
  Copy,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContractEditor = ({ quote, open, onClose }) => {
  const { toast } = useToast();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');

  const load = React.useCallback(async () => {
    if (!quote?.id) return;
    setLoading(true);
    try {
      // Always regenerate or fetch — endpoint is idempotent (upserts)
      const { data } = await axios.post(
        `${API}/contracts/from-quote/${quote.id}`,
        {},
        { withCredentials: true }
      );
      setContract(data);
    } catch (err) {
      toast({
        title: 'Could not load contract',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [quote?.id, toast]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  const updateSection = (idx, field, value) => {
    const next = [...contract.sections];
    next[idx] = { ...next[idx], [field]: value };
    setContract({ ...contract, sections: next });
  };

  const save = async () => {
    if (!contract) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/contracts/${contract.id}`,
        { sections: contract.sections, governing_state: contract.governing_state || null },
        { withCredentials: true }
      );
      setContract(data);
      toast({ title: 'Contract saved', description: 'Changes synced to the client portal.' });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Reset all sections to defaults? Your edits will be lost.')) return;
    setRegenerating(true);
    await load();
    setRegenerating(false);
    toast({ title: 'Contract reset', description: 'All sections reset to defaults.' });
  };

  const generatePortalLink = async () => {
    try {
      const { data } = await axios.post(
        `${API}/client-portal/quote/${quote.id}/portal-config`,
        { deposit_percent: quote.deposit_percent || 50, availability_slots: [] },
        { withCredentials: true }
      );
      setPortalUrl(data.portal_url);
      await navigator.clipboard.writeText(data.portal_url).catch(() => {});
      toast({ title: 'Portal link copied', description: data.portal_url });
    } catch (err) {
      toast({
        title: 'Could not get portal link',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-editor">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-blue-600" />
            <DialogTitle className="text-xl">Contract Editor</DialogTitle>
            <Badge className="ml-auto bg-blue-100 text-blue-800 hover:bg-blue-100">
              Signed via client portal
            </Badge>
          </div>
          <p className="text-sm text-slate-500">
            Review and edit the 8 sections below. They'll appear as an expandable contract in the Client Approval Portal — the client's signature covers both the quote and this contract.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-16 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
            <p className="text-sm text-slate-500 mt-2">Generating contract from quote…</p>
          </div>
        ) : !contract ? (
          <div className="py-16 text-center text-slate-500">Contract unavailable.</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={regenerate}
                disabled={regenerating}
                data-testid="contract-regenerate-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${regenerating ? 'animate-spin' : ''}`} />
                Reset to defaults
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={generatePortalLink}
                data-testid="contract-copy-link-btn"
              >
                <LinkIcon className="w-4 h-4 mr-1.5" />
                Copy portal link
              </Button>
              {portalUrl && (
                <span className="text-xs text-slate-500 truncate max-w-[200px]" title={portalUrl}>
                  {portalUrl}
                </span>
              )}
            </div>

            <div className="space-y-3">
              <Label htmlFor="governing-state" className="text-sm">Governing state</Label>
              <Input
                id="governing-state"
                placeholder="e.g., Illinois"
                value={contract.governing_state || ''}
                onChange={(e) => setContract({ ...contract, governing_state: e.target.value })}
                data-testid="contract-governing-state"
              />
            </div>

            {contract.sections.map((section, idx) => (
              <div
                key={section.key}
                className="border border-slate-200 rounded-lg p-4 space-y-2 bg-white"
                data-testid={`contract-section-${section.key}`}
              >
                <div className="flex items-center gap-2">
                  <Edit3 className="w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={section.title}
                    onChange={(e) => updateSection(idx, 'title', e.target.value)}
                    className="font-semibold text-slate-900 border-0 px-1 focus-visible:ring-1"
                  />
                </div>
                <Textarea
                  value={section.body}
                  onChange={(e) => updateSection(idx, 'body', e.target.value)}
                  rows={Math.min(8, Math.max(3, (section.body || '').split('\n').length + 1))}
                  className="text-sm text-slate-700"
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-2">
              <Button variant="outline" onClick={onClose} data-testid="contract-cancel-btn">
                Close
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="contract-save-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" /> Save contract</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ContractEditor;
